import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

/**
 * Мемоизированный вид запроса (F06) из результата `endpoint.select(args)`.
 * Мемоизация по полям, а не по объекту результата: `select` отдаёт новый
 * объект, и экран перерисовывался бы на каждое действие стора.
 */
export function makeQueryViewSelector<I, T>(
  pick: (input: I) => QueryResultLike<T> | undefined,
  isEmpty: (data: T) => boolean,
) {
  return createSelector(
    [
      (input: I) => pick(input)?.isUninitialized ?? true,
      (input: I) => pick(input)?.isLoading ?? false,
      (input: I) => pick(input)?.isError ?? false,
      (input: I) => pick(input)?.isSuccess ?? false,
      (input: I) => pick(input)?.error,
      (input: I) => pick(input)?.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        isEmpty,
      ),
  )
}

/** Данные запроса только в состоянии «готово»: ошибка при старых данных — ошибка (F06). */
export function readyData<T>(view: QueryView, data: T | undefined): T | undefined {
  return view.status === 'ready' || view.status === 'empty' ? data : undefined
}

/** Время для человека: «07.10 14:32». Часовой пояс — устройства. */
export function formatMoment(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
